import React, { useState, useEffect, useCallback } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  LineChart, Line, PieChart, Pie, Cell, Legend,
} from "recharts";
import { TrendingUp, Clock, FileText, CheckCircle, Users, AlertCircle, Download } from "lucide-react";

// ── Constants ──────────────────────────────────────────────────────────────
const STATUS_COLORS_MAP = {
  pending_review: "#D97706",
  in_progress: "#0284C7",
  completed: "#059669",
  cancelled: "#DC2626",
  on_hold: "#7C3AED",
};
const PRIORITY_COLORS_MAP = {
  urgent: "#DC2626",
  high: "#D97706",
  medium: "#0284C7",
  low: "#A8A29E",
  none: "#E7E5E4",
};
const CHART_AMBER = "#D97706";
const CHART_TEAL = "#0D9488";

const fmt = (s) => s?.split("_").map((w) => w[0].toUpperCase() + w.slice(1)).join(" ") || "";
const gbp = (n) => `£${(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PERIODS = [
  { label: "7D", days: 7 },
  { label: "30D", days: 30 },
  { label: "90D", days: 90 },
  { label: "6M", days: 182 },
  { label: "1Y", days: 365 },
  { label: "YTD", days: null, ytd: true },
  { label: "All", days: null, all: true },
];

function toDateStr(d) {
  return d.toISOString().slice(0, 10);
}

function getPeriodDates(period) {
  const today = new Date();
  const end = toDateStr(today);
  if (period.all) return { start: "2020-01-01", end };
  if (period.ytd) {
    const start = `${today.getFullYear()}-01-01`;
    return { start, end };
  }
  const start = toDateStr(new Date(today.getTime() - period.days * 86400000));
  return { start, end };
}

// ── Tooltip ─────────────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border rounded-xl px-3 py-2.5 text-xs shadow-lg" style={{ borderColor: "#E7E5E4" }}>
      <p className="font-semibold mb-1.5" style={{ color: "#292524" }}>{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.color || "#57534E" }}>
          {p.name}: {p.unit === "£" ? gbp(p.value) : `${p.value}${p.unit || ""}`}
        </p>
      ))}
    </div>
  );
};

// ── KPI Card ─────────────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, color = "#D97706", icon: Icon, warn }) {
  return (
    <div className="bg-white rounded-xl border p-5" style={{ borderColor: warn ? "#FCA5A5" : "#E7E5E4", background: warn ? "#FFF5F5" : "#fff" }}>
      <div className="flex items-start justify-between mb-3">
        <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#A8A29E" }}>{label}</p>
        {Icon && (
          <div className="p-1.5 rounded-lg" style={{ background: color + "20" }}>
            <Icon size={14} strokeWidth={1.5} style={{ color }} />
          </div>
        )}
      </div>
      <p className="font-serif text-2xl font-bold" style={{ color: warn ? "#DC2626" : "#292524" }}>{value}</p>
      {sub && <p className="text-xs mt-1" style={{ color: "#A8A29E" }}>{sub}</p>}
    </div>
  );
}

// ── Section Header ────────────────────────────────────────────────────────────
function Section({ title, icon: Icon, children }) {
  return (
    <div className="mb-10">
      <div className="flex items-center gap-2 mb-5">
        <Icon size={16} strokeWidth={1.5} style={{ color: "#D97706" }} />
        <h2 className="font-serif text-xl font-bold" style={{ color: "#292524" }}>{title}</h2>
      </div>
      {children}
    </div>
  );
}

// ── Chart Card ────────────────────────────────────────────────────────────────
function ChartCard({ title, children, className = "" }) {
  return (
    <div className={`bg-white rounded-xl border p-5 ${className}`} style={{ borderColor: "#E7E5E4" }}>
      <p className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: "#A8A29E" }}>{title}</p>
      {children}
    </div>
  );
}

const EMPTY = (
  <div className="h-48 flex items-center justify-center text-sm" style={{ color: "#C7C3BF" }}>
    No data for this period
  </div>
);

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Analytics() {
  const [selectedPeriod, setSelectedPeriod] = useState(PERIODS[2]); // 90D default
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [showCustom, setShowCustom] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let dates;
      if (showCustom && customStart && customEnd) {
        dates = { start: customStart, end: customEnd };
      } else {
        dates = getPeriodDates(selectedPeriod);
      }
      const r = await api.get(`/analytics?start=${dates.start}&end=${dates.end}`);
      setData(r.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod, showCustom, customStart, customEnd]);

  useEffect(() => { load(); }, [load]);

  const f = data?.financial;
  const t = data?.time;
  const tk = data?.tasks;
  const cl = data?.clients;

  // Pie data helpers
  const statusPieData = tk ? Object.entries(tk.by_status).map(([k, v]) => ({ name: fmt(k), value: v, key: k })) : [];
  const priorityPieData = tk ? Object.entries(tk.by_priority).filter(([, v]) => v > 0).map(([k, v]) => ({ name: fmt(k) || "None", value: v, key: k })) : [];
  const invoiceStatusData = f ? Object.entries(f.invoice_count).map(([k, v]) => ({ name: fmt(k), value: v, key: k })) : [];
  const billabilityData = t ? [
    { name: "Billable", value: t.billable_hours },
    { name: "Non-billable", value: t.non_billable_hours },
  ] : [];

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="analytics-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-serif text-3xl font-bold" style={{ color: "#292524" }}>Analytics</h1>
            <p className="text-sm mt-1" style={{ color: "#57534E" }}>
              {data ? `${data.period.start} — ${data.period.end}` : "Loading..."}
            </p>
          </div>
          {/* Period selector */}
          <div className="flex flex-wrap items-center gap-2">
            {PERIODS.map((p) => (
              <button
                key={p.label}
                data-testid={`period-${p.label}`}
                onClick={() => { setSelectedPeriod(p); setShowCustom(false); }}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
                style={{
                  background: !showCustom && selectedPeriod.label === p.label ? "#292524" : "#F5F3EF",
                  color: !showCustom && selectedPeriod.label === p.label ? "#fff" : "#57534E",
                }}
              >
                {p.label}
              </button>
            ))}
            <button
              data-testid="period-custom"
              onClick={() => setShowCustom(!showCustom)}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
              style={{ background: showCustom ? "#292524" : "#F5F3EF", color: showCustom ? "#fff" : "#57534E" }}
            >
              Custom
            </button>
          </div>
        </div>

        {/* Custom date inputs */}
        {showCustom && (
          <div className="flex flex-wrap items-center gap-3 mb-6 p-4 rounded-xl" style={{ background: "#F5F3EF" }}>
            <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)}
              className="text-sm border rounded-lg px-3 py-1.5" style={{ borderColor: "#E7E5E4" }} />
            <span className="text-sm" style={{ color: "#A8A29E" }}>to</span>
            <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)}
              className="text-sm border rounded-lg px-3 py-1.5" style={{ borderColor: "#E7E5E4" }} />
            <button
              onClick={load}
              disabled={!customStart || !customEnd}
              className="text-xs font-semibold px-4 py-1.5 rounded-lg"
              style={{ background: "#292524", color: "#fff" }}
            >
              Apply
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-32">
            <div className="w-8 h-8 border-2 border-stone-200 border-t-amber-500 rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* ── FINANCIAL ──────────────────────────────────────────────── */}
            <Section title="Financial" icon={FileText}>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
                <KpiCard label="Total Invoiced" value={gbp(f?.total_invoiced)} icon={FileText} color="#D97706" sub={`${f?.invoice_count ? Object.values(f.invoice_count).reduce((a, b) => a + b, 0) : 0} invoices`} />
                <KpiCard label="Collected" value={gbp(f?.total_collected)} icon={CheckCircle} color="#059669" sub={`${f?.collection_rate}% collection rate`} />
                <KpiCard label="Outstanding" value={gbp(f?.outstanding)} icon={Clock} color="#0284C7" />
                <KpiCard label="Overdue" value={gbp(f?.overdue)} icon={AlertCircle} color="#DC2626" warn={f?.overdue > 0} sub={f?.avg_invoice_value ? `Avg invoice: ${gbp(f.avg_invoice_value)}` : undefined} />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <ChartCard title="Revenue Trend" className="lg:col-span-2">
                  {f?.revenue_by_month?.length ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={f.revenue_by_month} barGap={4}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F5F3EF" vertical={false} />
                        <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#A8A29E" }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: "#A8A29E" }} axisLine={false} tickLine={false} tickFormatter={(v) => `£${(v / 1000).toFixed(0)}k`} width={40} />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill: "#FFF9F0" }} />
                        <Bar dataKey="invoiced" name="Invoiced" fill="#FDE68A" radius={[3, 3, 0, 0]} unit="£" />
                        <Bar dataKey="collected" name="Collected" fill={CHART_AMBER} radius={[3, 3, 0, 0]} unit="£" />
                        <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : EMPTY}
                </ChartCard>
                <ChartCard title="Invoice Status">
                  {invoiceStatusData.length ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie data={invoiceStatusData} dataKey="value" cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={3} label={({ name, value }) => `${name}: ${value}`} labelLine={false}>
                          {invoiceStatusData.map((e) => (
                            <Cell key={e.key} fill={STATUS_COLORS_MAP[e.key] || "#A8A29E"} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v, n) => [v, n]} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : EMPTY}
                </ChartCard>
              </div>
              {f?.revenue_by_client?.length > 0 && (
                <ChartCard title="Revenue by Client" className="mt-5">
                  <ResponsiveContainer width="100%" height={Math.max(160, f.revenue_by_client.length * 36)}>
                    <BarChart data={f.revenue_by_client} layout="vertical" barSize={14} barGap={4}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F5F3EF" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11, fill: "#A8A29E" }} axisLine={false} tickLine={false} tickFormatter={(v) => `£${(v / 1000).toFixed(0)}k`} />
                      <YAxis type="category" dataKey="client" tick={{ fontSize: 11, fill: "#57534E" }} axisLine={false} tickLine={false} width={100} />
                      <Tooltip content={<CustomTooltip />} cursor={{ fill: "#FFF9F0" }} />
                      <Bar dataKey="invoiced" name="Invoiced" fill="#FDE68A" radius={[0, 3, 3, 0]} unit="£" />
                      <Bar dataKey="collected" name="Collected" fill={CHART_AMBER} radius={[0, 3, 3, 0]} unit="£" />
                      <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              )}
            </Section>

            {/* ── TIME ───────────────────────────────────────────────────── */}
            <Section title="Time Tracking" icon={Clock}>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
                <KpiCard label="Total Hours" value={`${t?.total_hours}h`} icon={Clock} color="#0284C7" />
                <KpiCard label="Billable Hours" value={`${t?.billable_hours}h`} icon={TrendingUp} color="#059669" sub={`${t?.billable_rate}% billable`} />
                <KpiCard label="Non-Billable" value={`${t?.non_billable_hours}h`} icon={Clock} color="#D97706" />
                <KpiCard label="Billable Rate" value={`${t?.billable_rate}%`} icon={CheckCircle} color={t?.billable_rate >= 70 ? "#059669" : "#D97706"} sub={t?.billable_rate < 60 ? "Consider reviewing unbillable work" : undefined} />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <ChartCard title="Hours by Month" className="lg:col-span-2">
                  {t?.hours_by_month?.length ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={t.hours_by_month}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F5F3EF" vertical={false} />
                        <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#A8A29E" }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: "#A8A29E" }} axisLine={false} tickLine={false} unit="h" width={36} />
                        <Tooltip content={<CustomTooltip />} />
                        <Line type="monotone" dataKey="hours" name="Hours" stroke={CHART_AMBER} strokeWidth={2} dot={{ fill: CHART_AMBER, r: 3 }} unit="h" />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : EMPTY}
                </ChartCard>
                <ChartCard title="Billable vs Non-Billable">
                  {t?.total_hours > 0 ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie data={billabilityData} dataKey="value" cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={3}>
                          <Cell fill="#059669" />
                          <Cell fill="#E7E5E4" />
                        </Pie>
                        <Tooltip formatter={(v, n) => [`${v}h`, n]} />
                        <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : EMPTY}
                </ChartCard>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-5">
                {t?.hours_by_client?.length > 0 && (
                  <ChartCard title="Hours by Client">
                    <ResponsiveContainer width="100%" height={Math.max(160, t.hours_by_client.length * 36)}>
                      <BarChart data={t.hours_by_client} layout="vertical" barSize={14}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F5F3EF" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11, fill: "#A8A29E" }} axisLine={false} tickLine={false} unit="h" />
                        <YAxis type="category" dataKey="client" tick={{ fontSize: 11, fill: "#57534E" }} axisLine={false} tickLine={false} width={100} />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill: "#FFF9F0" }} />
                        <Bar dataKey="hours" name="Hours" fill={CHART_AMBER} radius={[0, 3, 3, 0]} unit="h" />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                )}
                {t?.hours_by_member?.length > 0 && (
                  <ChartCard title="Hours by Team Member">
                    <ResponsiveContainer width="100%" height={Math.max(160, t.hours_by_member.length * 36)}>
                      <BarChart data={t.hours_by_member} layout="vertical" barSize={14} barGap={4}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F5F3EF" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11, fill: "#A8A29E" }} axisLine={false} tickLine={false} unit="h" />
                        <YAxis type="category" dataKey="member" tick={{ fontSize: 11, fill: "#57534E" }} axisLine={false} tickLine={false} width={90} />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill: "#FFF9F0" }} />
                        <Bar dataKey="hours" name="Total" fill="#E7E5E4" radius={[0, 3, 3, 0]} unit="h" />
                        <Bar dataKey="billable" name="Billable" fill={CHART_TEAL} radius={[0, 3, 3, 0]} unit="h" />
                        <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                )}
              </div>
            </Section>

            {/* ── TASKS ──────────────────────────────────────────────────── */}
            <Section title="Tasks" icon={CheckCircle}>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
                <KpiCard label="Total Tasks" value={tk?.total} icon={CheckCircle} color="#0284C7" />
                <KpiCard label="Completed" value={tk?.completed} icon={CheckCircle} color="#059669" sub={`${tk?.completion_rate}% completion rate`} />
                <KpiCard label="Still Open" value={tk?.open} icon={Clock} color="#D97706" sub={tk?.avg_completion_days ? `Avg ${tk.avg_completion_days}d to complete` : undefined} />
                <KpiCard label="Overdue" value={tk?.overdue} icon={AlertCircle} color="#DC2626" warn={tk?.overdue > 0} />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <ChartCard title="Tasks Over Time" className="lg:col-span-2">
                  {tk?.tasks_by_month?.length ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={tk.tasks_by_month} barGap={4}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F5F3EF" vertical={false} />
                        <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#A8A29E" }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: "#A8A29E" }} axisLine={false} tickLine={false} allowDecimals={false} width={30} />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill: "#FFF9F0" }} />
                        <Bar dataKey="created" name="Created" fill="#DBEAFE" radius={[3, 3, 0, 0]} />
                        <Bar dataKey="completed" name="Completed" fill="#059669" radius={[3, 3, 0, 0]} />
                        <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : EMPTY}
                </ChartCard>
                <div className="flex flex-col gap-5">
                  <ChartCard title="By Status">
                    {statusPieData.length ? (
                      <ResponsiveContainer width="100%" height={150}>
                        <PieChart>
                          <Pie data={statusPieData} dataKey="value" cx="50%" cy="50%" innerRadius={35} outerRadius={60} paddingAngle={3}>
                            {statusPieData.map((e) => <Cell key={e.key} fill={STATUS_COLORS_MAP[e.key] || "#A8A29E"} />)}
                          </Pie>
                          <Tooltip formatter={(v, n) => [v, n]} />
                          <Legend iconSize={8} wrapperStyle={{ fontSize: 10 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : EMPTY}
                  </ChartCard>
                  <ChartCard title="By Priority">
                    {priorityPieData.length ? (
                      <ResponsiveContainer width="100%" height={150}>
                        <PieChart>
                          <Pie data={priorityPieData} dataKey="value" cx="50%" cy="50%" innerRadius={35} outerRadius={60} paddingAngle={3}>
                            {priorityPieData.map((e) => <Cell key={e.key} fill={PRIORITY_COLORS_MAP[e.key] || "#A8A29E"} />)}
                          </Pie>
                          <Tooltip formatter={(v, n) => [v, n]} />
                          <Legend iconSize={8} wrapperStyle={{ fontSize: 10 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : EMPTY}
                  </ChartCard>
                </div>
              </div>
              {tk?.tasks_by_client?.length > 0 && (
                <ChartCard title="Tasks by Client" className="mt-5">
                  <ResponsiveContainer width="100%" height={Math.max(120, tk.tasks_by_client.length * 32)}>
                    <BarChart data={tk.tasks_by_client} layout="vertical" barSize={14}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F5F3EF" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11, fill: "#A8A29E" }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <YAxis type="category" dataKey="client" tick={{ fontSize: 11, fill: "#57534E" }} axisLine={false} tickLine={false} width={100} />
                      <Tooltip content={<CustomTooltip />} cursor={{ fill: "#FFF9F0" }} />
                      <Bar dataKey="count" name="Tasks" fill={CHART_AMBER} radius={[0, 3, 3, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              )}
            </Section>

            {/* ── CLIENTS ────────────────────────────────────────────────── */}
            <Section title="Clients" icon={Users}>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                <KpiCard label="Total Clients" value={cl?.total} icon={Users} color="#D97706" />
                <KpiCard label="Active in Period" value={cl?.active_in_period} icon={TrendingUp} color="#059669" sub="With tasks or invoices" />
                <KpiCard label="With Overdue Invoices" value={cl?.with_overdue_invoices} icon={AlertCircle} color="#DC2626" warn={cl?.with_overdue_invoices > 0} />
              </div>
            </Section>
          </>
        )}
      </div>
    </Layout>
  );
}
